import React, { useState } from 'react';
// Styles are in index.css

// Helper function to format duration in minutes to "Xh Ym" format
const formatDuration = (minutes) => {
  if (!minutes || minutes < 0) return 'N/A';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

// Helper function to format time string to HH:MM
const formatTime = (timeStr) => {
  if (!timeStr) return 'N/A';
  if (typeof timeStr === 'string' && timeStr.includes(':')) {
    return timeStr.substring(0, 5);
  }
  return 'N/A';
};

// Helper function to get full airline name from code
const getAirlineName = (code) => {
  const airlineMap = {
    'AA': 'American Airlines',
    'BA': 'British Airways',
    'UA': 'United Airlines',
    'DL': 'Delta Air Lines',
    'VB': 'Virgin Atlantic',
    'VS': 'Virgin America',
    'AF': 'Air France',
    'LH': 'Lufthansa',
    'IB': 'Iberia',
    'KL': 'KLM Royal Dutch',
    'AC': 'Air Canada',
    'EK': 'Emirates',
    'QF': 'Qantas',
    'SQ': 'Singapore Airlines',
    'NH': 'All Nippon Airways',
    'JL': 'Japan Airlines',
    'CX': 'Cathay Pacific',
    'OZ': 'Asiana Airlines',
    'TK': 'Turkish Airlines',
    'EY': 'Etihad Airways',
  };
  return airlineMap[code] || code || 'Airline';
};

// Helper function to generate auto-description for pairing
const generateDescription = (pairing, index) => {
  if (!pairing || !pairing.outbound) {
    return `Option ${String.fromCharCode(65 + index)}`;
  }

  const outbound = pairing.outbound;
  const isNonstop = outbound.stops === 0 || outbound.stops === '0';
  const stopText = isNonstop ? 'nonstop' : `${outbound.stops} stop${outbound.stops > 1 ? 's' : ''}`;
  const airline = getAirlineName(outbound.airlineCode);

  return `${stopText.charAt(0).toUpperCase() + stopText.slice(1)} ${outbound.cabinClass || 'economy'} fare via ${airline}`;
};

// Individual flight detail component
const FlightDetail = ({ flight, isReturn }) => {
  if (!flight) {
    return <div className="flight-detail">Return flight not available</div>;
  }

  const isNonstop = flight.stops === 0 || flight.stops === '0';

  return (
    <div className="flight-detail">
      <div className="flight-section-title">{isReturn ? 'RETURN' : 'OUTBOUND'}</div>

      <div className="flight-main-info">
        <div className="airline-flight">
          <span className="airline-code">{flight.airlineCode}</span>
          <span className="flight-number">{flight.flightNumber}</span>
        </div>
        <div className="times">
          <span className="departure-time">{formatTime(flight.departureTime)}</span>
          <span className="arrow">→</span>
          <span className="arrival-time">{formatTime(flight.arrivalTime)}</span>
        </div>
      </div>

      <div className="airports">
        <span className="airport-code">{flight.departureAirport}</span>
        <span className="separator">→</span>
        <span className="airport-code">{flight.arrivalAirport}</span>
      </div>

      <div className="flight-attributes">
        <span className="attribute duration-badge">{formatDuration(flight.duration)}</span>
        {flight.cabinClass && <span className="attribute cabin-badge">{flight.cabinClass}</span>}
        {flight.aircraft && <span className="attribute aircraft-badge">{flight.aircraft}</span>}
      </div>

      <div className="flight-info-grid">
        {flight.baggage && (
          <div className="info-item">
            <span className="info-label">Baggage:</span>
            <span className="info-value">{flight.baggage}</span>
          </div>
        )}

        <div className="info-item">
          <span className="info-label">Stops:</span>
          <span className="info-value">
            {isNonstop ? 'Nonstop' : `${flight.stops} stop${flight.stops > 1 ? 's' : ''}`}
            {flight.stopCities && flight.stopCities.length > 0 && ` via ${flight.stopCities.join(', ')}`}
          </span>
        </div>
      </div>

      <div className="policy-badges">
        {flight.refundable && <span className="policy-badge refundable">Refundable</span>}
        {flight.changeable && <span className="policy-badge changeable">Changeable</span>}
      </div>

      {flight.price && (
        <div className="segment-price">
          ${typeof flight.price === 'number' ? flight.price.toFixed(2) : flight.price}
        </div>
      )}
    </div>
  );
};

// Option card component
const OptionCard = ({ pairing, index, searchParams, onSelect }) => {
  const [expanded, setExpanded] = useState(false);
  const [editingLabel, setEditingLabel] = useState(false);
  const [customLabel, setCustomLabel] = useState('');

  const optionLabel = customLabel || `Option ${String.fromCharCode(65 + index)}`;
  const description = generateDescription(pairing, index);
  const perPersonPrice = pairing.perPersonPrice || (pairing.combinedPrice / (searchParams.adults || 1));

  return (
    <div className="option-card">
      <div className="option-header">
        <div className="option-title-area">
          <h3 className="option-title">
            {editingLabel ? (
              <input
                type="text"
                className="label-input"
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                onBlur={() => setEditingLabel(false)}
                onKeyDown={(e) => e.key === 'Enter' && setEditingLabel(false)}
                autoFocus
              />
            ) : (
              <span>{optionLabel}</span>
            )}
          </h3>
          <button
            className="edit-label-btn"
            onClick={() => setEditingLabel(!editingLabel)}
            title="Edit label"
            aria-label="Edit option label"
          >
            ✎
          </button>
        </div>
        <div className="option-price-right">
          <div className="per-person-label">per person</div>
          <div className="per-person-price">
            ${typeof perPersonPrice === 'number' ? perPersonPrice.toFixed(2) : perPersonPrice}
          </div>
        </div>
      </div>

      <p className="option-description">{description}</p>

      <div className="flights-container">
        <FlightDetail flight={pairing.outbound} isReturn={false} />
        {pairing.return && <FlightDetail flight={pairing.return} isReturn={true} />}
      </div>

      <button
        className="expand-details-btn"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? '▼ Hide Details' : '▶ Full Details'}
      </button>

      {expanded && (
        <div className="expanded-details">
          <div className="details-section">
            <h4>Outbound Details</h4>
            {pairing.outbound && (
              <div className="detail-group">
                {pairing.outbound.schedules && (
                  <div className="detail-item">
                    <span className="detail-label">Schedule:</span>
                    <span className="detail-value">{pairing.outbound.schedules}</span>
                  </div>
                )}
                {pairing.outbound.fareBasis && (
                  <div className="detail-item">
                    <span className="detail-label">Fare Basis:</span>
                    <span className="detail-value">{pairing.outbound.fareBasis}</span>
                  </div>
                )}
                {pairing.outbound.seatsAvailable && (
                  <div className="detail-item">
                    <span className="detail-label">Seats Available:</span>
                    <span className="detail-value">{pairing.outbound.seatsAvailable}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {pairing.return && (
            <div className="details-section">
              <h4>Return Details</h4>
              <div className="detail-group">
                {pairing.return.schedules && (
                  <div className="detail-item">
                    <span className="detail-label">Schedule:</span>
                    <span className="detail-value">{pairing.return.schedules}</span>
                  </div>
                )}
                {pairing.return.fareBasis && (
                  <div className="detail-item">
                    <span className="detail-label">Fare Basis:</span>
                    <span className="detail-value">{pairing.return.fareBasis}</span>
                  </div>
                )}
                {pairing.return.seatsAvailable && (
                  <div className="detail-item">
                    <span className="detail-label">Seats Available:</span>
                    <span className="detail-value">{pairing.return.seatsAvailable}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <button
        className="select-btn"
        onClick={() => onSelect(pairing)}
      >
        Select
      </button>
    </div>
  );
};

// Price summary card component
const PriceSummaryCard = ({ pairing, index, searchParams, commissionRate }) => {
  const adults = searchParams.adults || 1;
  const children = searchParams.children || 0;
  const totalPax = adults + children;

  const outboundPerPerson = pairing.outbound?.price || 0;
  const returnPerPerson = pairing.return?.price || 0;
  const combinedPerPerson = pairing.perPersonPrice || (pairing.combinedPrice / adults);
  const totalPrice = pairing.combinedPrice || (combinedPerPerson * adults);

  const commissionAmount = (totalPrice * (commissionRate / 100));

  return (
    <div className="price-summary-card">
      <div className="summary-header">
        <span className="summary-title">Option {String.fromCharCode(65 + index)}</span>
      </div>

      <div className="summary-row">
        <span className="summary-label">Outbound:</span>
        <span className="summary-value">${typeof outboundPerPerson === 'number' ? outboundPerPerson.toFixed(2) : outboundPerPerson}</span>
      </div>

      {pairing.return && (
        <div className="summary-row">
          <span className="summary-label">Return:</span>
          <span className="summary-value">${typeof returnPerPerson === 'number' ? returnPerPerson.toFixed(2) : returnPerPerson}</span>
        </div>
      )}

      <div className="summary-row total-row">
        <span className="summary-label">Combined / pp:</span>
        <span className="summary-value">${typeof combinedPerPerson === 'number' ? combinedPerPerson.toFixed(2) : combinedPerPerson}</span>
      </div>

      <div className="summary-row subtotal-row">
        <span className="summary-label">× {totalPax} pax:</span>
        <span className="summary-value">${typeof totalPrice === 'number' ? totalPrice.toFixed(2) : totalPrice}</span>
      </div>

      {commissionRate > 0 && (
        <div className="summary-row commission-row">
          <span className="summary-label">Commission ({commissionRate}%):</span>
          <span className="summary-value commission-value">
            +${typeof commissionAmount === 'number' ? commissionAmount.toFixed(2) : commissionAmount}
          </span>
        </div>
      )}
    </div>
  );
};

// Normalize a leg object from PairingMatrix format to flat format for display
// PairingMatrix provides: { id, leg: { schedules, elapsedTime }, price, schedule: { departure, arrival, carrier, equipment }, baseFare, taxes }
// We need: { airlineCode, flightNumber, departureAirport, arrivalAirport, departureTime, arrivalTime, duration, stops, aircraft, price, schedules, ... }
const normalizeFlight = (flight) => {
  if (!flight) return null;
  // If already normalized (has airlineCode), return as-is
  if (flight.airlineCode) return flight;

  const leg = flight.leg || {};
  const schedules = leg.schedules || [];
  const firstSeg = schedules[0] || flight.schedule || {};
  const lastSeg = schedules[schedules.length - 1] || firstSeg;
  const firstDeparture = firstSeg.departure || {};
  const lastArrival = lastSeg.arrival || {};
  const carrier = firstSeg.carrier || {};
  const stopCount = Math.max(0, schedules.length - 1);

  // Build flight number display: single segment = just number, multi = combined
  const flightNumDisplay = schedules.length <= 1
    ? (carrier.marketingFlightNumber || '')
    : schedules.map(s => `${s.carrier?.marketing || ''}${s.carrier?.marketingFlightNumber || ''}`).join(' / ');

  return {
    ...flight,
    airlineCode: carrier.marketing || '',
    flightNumber: flightNumDisplay,
    departureAirport: firstDeparture.airport || '',
    arrivalAirport: lastArrival.airport || '',
    departureTime: firstDeparture.time || '',
    arrivalTime: lastArrival.time || '',
    duration: leg.elapsedTime || 0,
    stops: stopCount,
    stopCities: stopCount > 0 ? schedules.slice(0, -1).map(s => s.arrival?.airport).filter(Boolean) : [],
    aircraft: firstSeg.equipment || '',
    cabinClass: schedules[0]?.cabin || '',
    price: flight.price || 0,
    baseFare: flight.baseFare || 0,
    taxes: flight.taxes || 0,
    schedules,
  };
};

// Normalize an entire pairing object
const normalizePairing = (pairing) => {
  if (!pairing) return pairing;
  return {
    ...pairing,
    outbound: normalizeFlight(pairing.outbound),
    return: normalizeFlight(pairing.return),
  };
};

// Main component
const ComparativeQuote = ({ pairings: rawPairings = [], searchParams = {}, onBack, onSelectOption }) => {
  const pairings = rawPairings.map(normalizePairing);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const [commissionRate, setCommissionRate] = useState(10);
  const [advisorNotes, setAdvisorNotes] = useState('');

  if (!pairings || pairings.length === 0) {
    return (
      <div className="comparative-quote">
        <div className="error-state">
          <p>No flight options available for comparison.</p>
          <button className="back-btn" onClick={onBack}>← Back to Search</button>
        </div>
      </div>
    );
  }

  const { origin, destination, departureDate, returnDate, adults = 1, children = 0, cabinClass = 'Economy' } = searchParams;
  const routeDisplay = returnDate ? `${origin} → ${destination} → ${origin}` : `${origin} → ${destination}`;
  const dateDisplay = returnDate ? `${departureDate} to ${returnDate}` : departureDate;
  const passengerCount = `${adults} adult${adults > 1 ? 's' : ''}${children > 0 ? `, ${children} child${children > 1 ? 'ren' : ''}` : ''}`;

  return (
    <div className="comparative-quote">
      {/* Header */}
      <div className="quote-header">
        <button className="back-btn" onClick={onBack} aria-label="Back to search">
          ← Back to Search
        </button>

        <div className="header-content">
          <h1 className="quote-title">Comparative Quote</h1>
          <div className="route-info">
            <span className="route">{routeDisplay}</span>
            <span className="separator">•</span>
            <span className="dates">{dateDisplay}</span>
            <span className="separator">•</span>
            <span className="passengers">{passengerCount}</span>
            <span className="separator">•</span>
            <span className="cabin">{cabinClass}</span>
          </div>
          <div className="comparison-meta">
            {pairings.length} itinerarie{pairings.length !== 1 ? 's' : ''} compared
          </div>
        </div>
      </div>

      {/* View Toggle */}
      <div className="view-toggle">
        <button
          className={`toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
          onClick={() => setViewMode('cards')}
        >
          Cards
        </button>
        <button
          className={`toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
          onClick={() => setViewMode('table')}
        >
          Table
        </button>
      </div>

      {/* Action Bar */}
      <div className="action-bar">
        <button className="action-btn save-draft-btn" disabled title="Coming soon">
          Save Draft
        </button>
        <button className="action-btn export-pdf-btn" disabled title="Coming soon">
          Export PDF
        </button>
        <button className="action-btn send-advisor-btn primary" disabled title="Coming soon">
          Send to Advisor
        </button>
      </div>

      {/* Main Content */}
      {viewMode === 'cards' ? (
        <div className="cards-view">
          <div className="options-grid">
            {pairings.map((pairing, index) => (
              <OptionCard
                key={index}
                pairing={pairing}
                index={index}
                searchParams={searchParams}
                onSelect={onSelectOption}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="table-view">
          <TableComparison pairings={pairings} searchParams={searchParams} />
        </div>
      )}

      {/* Commission Calculator */}
      <div className="commission-calc">
        <h2 className="calc-title">Commission Calculator</h2>
        <div className="commission-inputs">
          <label className="commission-label">
            <span>Commission Rate (%):</span>
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={commissionRate}
              onChange={(e) => setCommissionRate(parseFloat(e.target.value) || 0)}
              className="commission-input"
            />
          </label>
        </div>

        <div className="price-summaries">
          {pairings.map((pairing, index) => (
            <PriceSummaryCard
              key={index}
              pairing={pairing}
              index={index}
              searchParams={searchParams}
              commissionRate={commissionRate}
            />
          ))}
        </div>
      </div>

      {/* Advisor Notes */}
      <div className="advisor-notes-section">
        <h2 className="notes-title">Advisor Notes</h2>
        <textarea
          className="notes-textarea"
          placeholder="Add notes about these itineraries for your records or to include in communication with the client..."
          value={advisorNotes}
          onChange={(e) => setAdvisorNotes(e.target.value)}
          rows={4}
        />
      </div>
    </div>
  );
};

// Table Comparison Component
const TableComparison = ({ pairings, searchParams }) => {
  const attributes = [
    'Airline',
    'Flight Number',
    'Cabin',
    'Departure Time',
    'Arrival Time',
    'Duration',
    'Stops',
    'Aircraft',
    'Baggage',
    'Refundable',
    'Changeable',
    'Price',
  ];

  const getCellValue = (flight, attribute) => {
    if (!flight) return '—';

    switch (attribute) {
      case 'Airline':
        return getAirlineName(flight.airlineCode);
      case 'Flight Number':
        return flight.flightNumber;
      case 'Cabin':
        return flight.cabinClass || '—';
      case 'Departure Time':
        return `${formatTime(flight.departureTime)} ${flight.departureAirport}`;
      case 'Arrival Time':
        return `${formatTime(flight.arrivalTime)} ${flight.arrivalAirport}`;
      case 'Duration':
        return formatDuration(flight.duration);
      case 'Stops':
        return flight.stops === 0 || flight.stops === '0'
          ? 'Nonstop'
          : `${flight.stops} stop${flight.stops > 1 ? 's' : ''}`;
      case 'Aircraft':
        return flight.aircraft || '—';
      case 'Baggage':
        return flight.baggage || '—';
      case 'Refundable':
        return flight.refundable ? 'Yes' : 'No';
      case 'Changeable':
        return flight.changeable ? 'Yes' : 'No';
      case 'Price':
        return flight.price ? `$${typeof flight.price === 'number' ? flight.price.toFixed(2) : flight.price}` : '—';
      default:
        return '—';
    }
  };

  return (
    <div className="table-comparison">
      {/* Outbound Section */}
      <div className="table-section">
        <h3 className="section-title">Outbound Flights</h3>
        <table className="comparison-table">
          <thead>
            <tr>
              <th className="attribute-column">Attribute</th>
              {pairings.map((_, index) => (
                <th key={index} className="option-column">
                  Option {String.fromCharCode(65 + index)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {attributes.map((attr) => (
              <tr key={attr}>
                <td className="attribute-name">{attr}</td>
                {pairings.map((pairing, index) => (
                  <td key={index} className="cell-value">
                    {getCellValue(pairing.outbound, attr)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Return Section (if applicable) */}
      {pairings.some((p) => p.return) && (
        <div className="table-section">
          <h3 className="section-title">Return Flights</h3>
          <table className="comparison-table">
            <thead>
              <tr>
                <th className="attribute-column">Attribute</th>
                {pairings.map((_, index) => (
                  <th key={index} className="option-column">
                    Option {String.fromCharCode(65 + index)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {attributes.map((attr) => (
                <tr key={attr}>
                  <td className="attribute-name">{attr}</td>
                  {pairings.map((pairing, index) => (
                    <td key={index} className="cell-value">
                      {getCellValue(pairing.return, attr)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Price Summary Section */}
      <div className="table-section">
        <h3 className="section-title">Price Summary</h3>
        <table className="comparison-table">
          <thead>
            <tr>
              <th className="attribute-column">Price Details</th>
              {pairings.map((_, index) => (
                <th key={index} className="option-column">
                  Option {String.fromCharCode(65 + index)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="attribute-name">Combined Price</td>
              {pairings.map((pairing, index) => (
                <td key={index} className="cell-value price-cell">
                  ${typeof pairing.combinedPrice === 'number'
                    ? pairing.combinedPrice.toFixed(2)
                    : pairing.combinedPrice}
                </td>
              ))}
            </tr>
            <tr>
              <td className="attribute-name">Per Person Price</td>
              {pairings.map((pairing, index) => {
                const perPersonPrice =
                  pairing.perPersonPrice ||
                  pairing.combinedPrice / (searchParams.adults || 1);
                return (
                  <td key={index} className="cell-value price-cell">
                    ${typeof perPersonPrice === 'number'
                      ? perPersonPrice.toFixed(2)
                      : perPersonPrice}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ComparativeQuote;
